import type { Attachment } from '@medplum/fhirtypes'
import { IconAlertTriangle, IconVideo } from '@tabler/icons-react'
import type { JSX } from 'react'
import { AttachmentCardLayout } from '#src/components/attachments/AttachmentCardLayout.tsx'
import { RetryConversionButton } from '#src/components/attachments/RetryConversionButton.tsx'
import { Skeleton } from '#src/components/core/Skeleton.tsx'
import { useAttachmentConversionStatus } from '#src/hooks/useAttachmentConversionStatus.ts'

interface AttachmentVideoDisplayProps {
  attachment: Attachment
  url: string
  onTitleChange?: (title: string) => void
  /** Parent DocumentReference id; required to surface the retry-conversion button. */
  documentReferenceId?: string
}

export function AttachmentVideoDisplay({
  attachment,
  url,
  onTitleChange,
  documentReferenceId,
}: AttachmentVideoDisplayProps): JSX.Element {
  const title = attachment.title || 'Video'
  const { status } = useAttachmentConversionStatus(attachment)

  let preview: JSX.Element
  if (status === 'pending') {
    preview = (
      <div className="relative h-48 w-full">
        <Skeleton
          role="status"
          aria-label="File converting"
          className="h-full w-full"
        />
        <p className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
          Converting...
        </p>
      </div>
    )
  } else if (status === 'failed') {
    preview = (
      <div
        role="alert"
        className="flex flex-col items-center justify-center gap-2 py-8 text-sm"
      >
        <IconAlertTriangle className="h-6 w-6 text-amber-500" />
        <p className="text-muted-foreground">
          Preview unavailable — this file couldn&apos;t be converted
        </p>
        <RetryConversionButton
          attachment={attachment}
          documentReferenceId={documentReferenceId}
        />
      </div>
    )
  } else {
    preview = (
      <video className="max-h-[500px] w-full" controls>
        <source type={attachment.contentType} src={url} />
        <track kind="captions" />
      </video>
    )
  }

  return (
    <AttachmentCardLayout
      attachment={attachment}
      title={title}
      icon={<IconVideo className="h-4 w-4" />}
      onTitleChange={onTitleChange}
      conversionStatus={status}
      preview={preview}
    />
  )
}
